import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type GridApi, type GridReadyEvent, type CellValueChangedEvent, type CellStyle } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button, Select, MenuItem, FormControl } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import AggridCheckbox from "@components/aggrid/aggrid_checkbox";
import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

interface State {
  gridApi: GridApi | null,
}

const defState: State = {
  gridApi: null,
};

interface SearchParams {
  userAuth: string
}

const defSearchParams: SearchParams = {
  userAuth: ""
};

interface Result {
  searchParams: SearchParams,
  infos: any[]
}

const defResult: Result = {
  searchParams: kutil.deepCopy(defSearchParams),
  infos: []
};

const defColDef = { ...kutil.defaultColDef, sortable: false };

function AuthManage(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const tmpCellStyle1: CellStyle = { textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" };
  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    {
      headerName: "메뉴", field: "menuName", width: 400, cellStyle: { textAlign: "left" },
      cellRenderer: (p: any) => {
        const info = p.data;
        let cellHtml = "";
        for (let i = 0; i < info.depth; ++i) {
          cellHtml += "    ";
        }
        if (info.depth === 2) {
          cellHtml += "- ";
        } else if (info.depth >= 3) {
          cellHtml += "* ";
        }
        cellHtml += info.menuName;
        return cellHtml;
      },
    },
    { headerName: "조회", field: "isViewAuth", width: 120, cellStyle: tmpCellStyle1, editable: true },
    { headerName: "등록/수정", field: "isWriteAuth", width: 120, cellStyle: tmpCellStyle1, editable: true },
    { headerName: "삭제", field: "isDeleteAuth", width: 120, cellStyle: tmpCellStyle1, editable: true },
    { headerName: "다운로드", field: "isDownloadAuth", width: 120, cellStyle: tmpCellStyle1, editable: true },
    { headerName: "기능1", field: "isFunc1Auth", width: 120, cellStyle: tmpCellStyle1,
      cellRenderer: AggridCheckbox,
      cellRendererParams: (p: any) => ({ label: p.data.func1Name, isVisible: (d: any) => !!d.func1Name })
    },
    { headerName: "기능2", field: "isFunc2Auth", width: 120, cellStyle: tmpCellStyle1,
      cellRenderer: AggridCheckbox,
      cellRendererParams: (p: any) => ({ label: p.data.func2Name, isVisible: (d: any) => !!d.func2Name })
    },
    { headerName: "기능3", field: "isFunc3Auth", width: 120, cellStyle: tmpCellStyle1,
      cellRenderer: AggridCheckbox,
      cellRendererParams: (p: any) => ({ label: p.data.func3Name, isVisible: (d: any) => !!d.func3Name })
    },
    { headerName: "기능4", field: "isFunc4Auth", width: 120, cellStyle: tmpCellStyle1,
      cellRenderer: AggridCheckbox,
      cellRendererParams: (p: any) => ({ label: p.data.func4Name, isVisible: (d: any) => !!d.func4Name })
    },
    { headerName: "기능5", field: "isFunc5Auth", width: 120, cellStyle: tmpCellStyle1,
      cellRenderer: AggridCheckbox,
      cellRendererParams: (p: any) => ({ label: p.data.func5Name, isVisible: (d: any) => !!d.func5Name })
    }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    getRowId: (params: any) => params.data.menuId
  }), []);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    },
    onCellValueChanged: (e: CellValueChangedEvent) => {
      const field = e.column.getColId();
      const menu = e.data;
      const api = state.gridApi;

      _setFieldForChild(api, menu, field);

      if (menu.parentMenuId) {
        _setFieldForParent(api, menu, field);
      }

      api?.refreshCells({ force: true });
    }
  });

  function _setFieldForChild(api: any, menu: any, field: string) {
    api.forEachNode((node: any) => {
      if (menu.menuId === node.data.parentMenuId) {
        node.data[field] = menu[field];
        _setFieldForChild(api, node.data, field);
      }
    });
  }

  function _setFieldForParent(api: any, menu: any, field: string) {
    if (!menu.parentMenuId) return;
    let isAllChecked = true;
    api.forEachNode((node: any) => {
      if (menu.parentMenuId === node.data.parentMenuId) {
        if (node.data[field] === false) isAllChecked = false;
      }
    });
    const parentMenu = api.getRowNode(menu.parentMenuId).data;
    parentMenu[field] = isAllChecked;
    _setFieldForParent(api, parentMenu, field);
  }

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);

    if (!searchParams.userAuth) {
      searchParams.userAuth = (commonCode.userAuth as any[])?.[0]?.code ?? "";
    }

    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      if (!reqSearchParams.userAuth) {
        return;
      }

      const data = await kutil.sendGet("/api/system/auth_manage.data?mode=search", reqSearchParams, {});
      const menuInfosByMenuId: any = {};
      const menuInfos = data.infos.map((resInfo: any) => {
        const info = kutil.copyObj<any, any>(resInfo, {children: []});
        menuInfosByMenuId[info.menuId] = info;
        if (info.parentMenuId) {
          const parentInfo = menuInfosByMenuId[info.parentMenuId];
          info.depth = parentInfo.depth + 1;
          parentInfo.children.push(info);
        } else {
          info.depth = 1;
        }
        info.userAuth = searchParams.userAuth;
        info.isViewAuth = info.viewAuthYn == "Y";
        info.isWriteAuth = info.writeAuthYn == "Y";
        info.isDeleteAuth = info.deleteAuthYn == "Y";
        info.isDownloadAuth = info.downloadAuthYn == "Y";
        info.isFunc1Auth = info.func1AuthYn == "Y";
        info.isFunc2Auth = info.func2AuthYn == "Y";
        info.isFunc3Auth = info.func3AuthYn == "Y";
        info.isFunc4Auth = info.func4AuthYn == "Y";
        info.isFunc5Auth = info.func5AuthYn == "Y";
        return info;
      });

      data.infos = menuInfos;
      data.infos.filter((info: any) => info.children.length > 0).forEach((parentMenu: any) => {
        parentMenu.isViewAuth = data.infos.filter((info: any) => info.parentMenuId == parentMenu.menuId).every((info: any) => info.isViewAuth);
        parentMenu.isWriteAuth = data.infos.filter((info: any) => info.parentMenuId == parentMenu.menuId).every((info: any) => info.isWriteAuth);
        parentMenu.isDeleteAuth = data.infos.filter((info: any) => info.parentMenuId == parentMenu.menuId).every((info: any) => info.isDeleteAuth);
        parentMenu.isDownloadAuth = data.infos.filter((info: any) => info.parentMenuId == parentMenu.menuId).every((info: any) => info.isDownloadAuth);
        parentMenu.isFunc1Auth = data.infos.filter((info: any) => info.parentMenuId == parentMenu.menuId).every((info: any) => info.isFunc1Auth);
        parentMenu.isFunc2Auth = data.infos.filter((info: any) => info.parentMenuId == parentMenu.menuId).every((info: any) => info.isFunc2Auth);
        parentMenu.isFunc3Auth = data.infos.filter((info: any) => info.parentMenuId == parentMenu.menuId).every((info: any) => info.isFunc3Auth);
        parentMenu.isFunc4Auth = data.infos.filter((info: any) => info.parentMenuId == parentMenu.menuId).every((info: any) => info.isFunc4Auth);
        parentMenu.isFunc5Auth = data.infos.filter((info: any) => info.parentMenuId == parentMenu.menuId).every((info: any) => info.isFunc5Auth);
      });
      setResult({
        searchParams: reqSearchParams,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }
  
  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams });
    router.navigateByPath("/system/auth_manage", searchParams);
  }

  async function save() {
    if (await kutil.confirm("저장하시겠습니까?") == false) {
      return false;
    }

    try {
      const api = state.gridApi;
      if (!api) return false;

      result.infos.forEach((info) => {
        const node = api.getRowNode(info.menuId);
        if (!node) return;

        kutil.copyObj(node.data, info, ["menuId"]);
        info.viewAuthYn = info.isViewAuth ? "Y" : "N";
        info.writeAuthYn = info.isWriteAuth ? "Y" : "N";
        info.deleteAuthYn = info.isDeleteAuth ? "Y" : "N";
        info.downloadAuthYn = info.isDownloadAuth ? "Y" : "N";
        info.func1AuthYn = info.isFunc1Auth ? "Y" : "N";
        info.func2AuthYn = info.isFunc2Auth ? "Y" : "N";
        info.func3AuthYn = info.isFunc3Auth ? "Y" : "N";
        info.func4AuthYn = info.isFunc4Auth ? "Y" : "N";
        info.func5AuthYn = info.isFunc5Auth ? "Y" : "N";
      });
      const data = await kutil.sendPost("/api/system/auth_manage.proc?mode=save", result.infos, {
      });
      await kutil.alert("저장하였습니다.");
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents list">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>권한 관리</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            { auth.write &&
            <Button variant="contained" onClick={save}>저장</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="srch-area">
          <div>
            <p>권한</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.userAuth}
                onChange={(e) => {doSearch(e, {userAuth: e.target.value});}}
                displayEmpty
              >
              {(commonCode.userAuth as any[]).map((code) => (
                <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
              ))}
              </Select>
            </FormControl>
          </div>
        </div>
        {/*// 검색 */}
        {/* 그리드 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={defColDef}
              initialState={gridState}
              getRowId={gridOptions.getRowId}
              onGridReady={gridCallbacks.onGridReady}
              onStateUpdated={gridCallbacks.onStateUpdated}
              onCellValueChanged={gridCallbacks.onCellValueChanged}
            />
          </div>
        </AgGridProvider>
        {/*// 그리드 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(AuthManage);
